import { API_BASE_URL } from '../config/api';

interface GenerateUploadUrlParams {
  fileName: string;
  videoId: string;
}

interface GenerateUploadUrlResponse {
  uploadUrl: string;
  location: string;
}

interface UploadAudioParams {
  file: File;
  uploadUrl: string;
}

interface AudioUploadResult {
  uploadUrl: string;
  location: string;
  success: boolean;
}

export const generateUploadUrl = async (
  params: GenerateUploadUrlParams,
  token: string
): Promise<GenerateUploadUrlResponse> => {
  try {
    const url = `${API_BASE_URL}/storage/generateUploadUrl?fileName=${encodeURIComponent(
      params.fileName
    )}&videoId=${encodeURIComponent(params.videoId)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to generate upload URL: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Generate Upload URL Error:', error);
    throw error;
  }
};

export const uploadAudio = async (
  params: UploadAudioParams
): Promise<boolean> => {
  try {
    const response = await fetch(params.uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': params.file.type || 'audio/mpeg',
      },
      body: params.file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload audio: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('Audio Upload Error:', error);
    throw error;
  }
};

export const generateDownloadUrl = async (
  location: string,
  token: string
): Promise<string> => {
  const response = await fetch(
    `${API_BASE_URL}/storage/generateDownloadUrl?location=${encodeURIComponent(location)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch download URL');
  }

  const { downloadUrl } = await response.json();
  return downloadUrl;
};

/**
 * Uploads an audio file and returns the relative blob location (not a signed
 * download URL). The caller stores this path on the video document and the
 * frontend resolves it into a playable URL using the video's SAS token
 * (`getAssetDownloadUrl`), mirroring how background/video assets are handled.
 */
export const handleAudioUpload = async (
  file: File,
  videoId: string,
  token: string
): Promise<AudioUploadResult> => {
  const uploadUrlData = await generateUploadUrl(
    {
      fileName: file.name,
      videoId,
    },
    token
  );

  const uploadSuccess = await uploadAudio({
    file,
    uploadUrl: uploadUrlData.uploadUrl,
  });

  if (!uploadSuccess) {
    throw new Error('Audio upload failed');
  }

  return {
    ...uploadUrlData,
    success: uploadSuccess,
  };
};

export const handleUploadAudio = async (audioFile: File, videoId: string) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    try {
      const result = await handleAudioUpload(audioFile, videoId, token);
      if (result.success) {
        return result.location;
      }
    } catch (error) {
      console.error('Failed to upload audio:', error);
    }
  } else {
    console.error('Token not found');
    return null;
  }
};
